// src/context/TaskContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";

/* -------------------------------------------------------------------------- */
/*  Context & Defaults                                                        */
/* -------------------------------------------------------------------------- */
const defaultCtx = {
  listsGlobal: [],
  tasksGlobal: [],
  groupsGlobal: [],
  listColors: {},
  createOrUpdateList: async () => {},
  deleteList: async () => {},
  createTask: async () => {},
  updateTask: async () => {},
  deleteTask: async () => {},
};

const TaskContext = createContext(defaultCtx);
export const useTaskContext = () => useContext(TaskContext);

/* -------------------------------------------------------------------------- */
/*  Provider                                                                  */
/* -------------------------------------------------------------------------- */
export const TaskProvider = ({ children }) => {
  const { user, teamOwnerId, loading: authLoading } = useAuth();

  /* -------------------------------- state -------------------------------- */
  const [listsGlobal, setListsGlobal] = useState([]);
  const [tasksGlobal, setTasksGlobal] = useState([]);
  const [groupsGlobal, setGroupsGlobal] = useState([]);
  const [listColors, setListColors] = useState({});

  const ownerUid = user ? teamOwnerId || user.uid : null;

  /* ----------------------------------------------------------------------- */
  /*  LISTAS (normales & rápidas)                                            */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    if (!ownerUid) return;

    const qProject = query(
      collection(db, "list"),
      where("ownerId", "==", ownerUid)
    );
    const unsubProject = onSnapshot(qProject, (snap) => {
      const lists = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        isFast: false,
      }));
      mergeLists(lists);
    });

    const qFast = query(
      collection(db, "fastLists"),
      where("ownerId", "==", ownerUid)
    );
    const unsubFast = onSnapshot(qFast, (snap) => {
      const lists = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        projectId: null,
        isFast: true,
      }));
      mergeLists(lists);
    });

    return () => {
      unsubProject();
      unsubFast();
    };
  }, [ownerUid]);

  const mergeLists = (incoming) => {
    setListsGlobal((prev) => {
      const withoutDup = [
        ...prev.filter((p) => !incoming.some((n) => n.id === p.id)),
        ...incoming,
      ];
      const colors = {};
      withoutDup.forEach((l) => (colors[l.name] = l.color || "#007bff"));
      setListColors(colors);
      return withoutDup;
    });
  };

  /* ----------------------------------------------------------------------- */
  /*  TAREAS normales (colección raíz "tareas")                              */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    if (!ownerUid) return;

    const q = query(collection(db, "tareas"), where("ownerId", "==", ownerUid));
    const unsub = onSnapshot(q, (snap) => {
      const normalTasks = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt,
        comments: d.data().comments || [],
        attachments: d.data().attachments || [],
        avatarUrl: d.data().avatarUrl || "/img/avatar2.jpg",
        isFast: false,
      }));

      setTasksGlobal((prev) => {
        const fast = prev.filter((t) => t.isFast);
        return [...fast, ...normalTasks];
      });
    });

    return () => unsub();
  }, [ownerUid]);

  /* ----------------------------------------------------------------------- */
  /*  TAREAS rápidas (fastLists/{id}/tasks)                                  */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    if (!ownerUid) return;

    const qFastLists = query(
      collection(db, "fastLists"),
      where("ownerId", "==", ownerUid)
    );
    const unsubFastLists = onSnapshot(qFastLists, (listsSnap) => {
      const innerUnsubs = [];

      listsSnap.docs.forEach((listDoc) => {
        const tasksCol = collection(db, "fastLists", listDoc.id, "tasks");

        const unsubTasks = onSnapshot(tasksCol, (tasksSnap) => {
          const fastTasks = tasksSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            listId: listDoc.id,
            isFast: true,
            avatarUrl: d.data().avatarUrl || "/img/avatar2.jpg",
            comments: d.data().comments || [],
            attachments: d.data().attachments || [],
          }));

          setTasksGlobal((prev) => {
            const others = prev.filter(
              (t) => !(t.isFast && t.listId === listDoc.id)
            );
            return [...others, ...fastTasks];
          });
        });

        innerUnsubs.push(unsubTasks);
      });

      return () => innerUnsubs.forEach((fn) => fn());
    });

    return () => unsubFastLists();
  }, [ownerUid]);

  /* ----------------------------------------------------------------------- */
  /*  GRUPOS / PROYECTOS                                                     */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    if (!ownerUid) return;
    const grpQ = query(
      collection(db, "gruposTareas"),
      where("ownerId", "==", ownerUid)
    );
    const unsub = onSnapshot(grpQ, (grpSnap) => {
      const temp = [];
      const cleaners = grpSnap.docs.map((gd) => {
        const groupData = { id: gd.id, ...gd.data(), projects: [] };
        const projQ = query(
          collection(db, "gruposTareas", gd.id, "projects"),
          where("ownerId", "==", ownerUid)
        );
        return onSnapshot(projQ, (projSnap) => {
          groupData.projects = projSnap.docs.map((p) => ({
            id: p.id,
            ...p.data(),
          }));
          const i = temp.findIndex((g) => g.id === groupData.id);
          if (i >= 0) temp[i] = groupData;
          else temp.push(groupData);
          setGroupsGlobal([...temp]);
        });
      });
      return () => cleaners.forEach((c) => c());
    });
    return () => unsub();
  }, [ownerUid]);

  /* ----------------------------------------------------------------------- */
  /*  CRUD LISTA (crea / edita)                                              */
  /* ----------------------------------------------------------------------- */
  const createOrUpdateList = async ({
    name,
    color,
    projectId, // undefined / null  ⇒ fastList
    ownerUid, // uid real del dueño de la lista
    oldName = null, // ≠ null             ⇒ renombrar
  }) => {
    if (!ownerUid) return false;
    if (!name.trim()) {
      Swal.fire("El nombre no puede estar vacío", "", "error");
      return false;
    }

    const col = projectId ? "list" : "fastLists";
    const listsRef = collection(db, col);

    try {
      /* ¿Existe ya una lista con ese nombre (o con el nombre antiguo)? */
      const dup = await getDocs(
        query(
          listsRef,
          where("name", "==", oldName || name),
          where("ownerId", "==", ownerUid),
          projectId
            ? where("projectId", "==", projectId)
            : where("projectId", "==", null)
        )
      );

      /* ------------------------------------------------------------------ */
      /*  RENOMBRAR                                                         */
      /* ------------------------------------------------------------------ */
      if (oldName) {
        if (dup.empty) {
          await Swal.fire("No se encontró la lista", "", "error");
          return false;
        }

        const listRef = dup.docs[0].ref;
        await updateDoc(listRef, { name, color });

        /* renombrar todas las tareas que estaban en esa lista */
        const batch = writeBatch(db);

        if (projectId) {
          /* listas normales  →  tareas en colección raíz `tareas` */
          const tSnap = await getDocs(
            query(
              collection(db, "tareas"),
              where("status", "==", oldName),
              where("projectId", "==", projectId),
              where("ownerId", "==", ownerUid)
            )
          );
          tSnap.forEach((t) => batch.update(t.ref, { status: name }));
        } else {
          /* fastLists  →  sub-colección fastLists/{id}/tasks */
          const fastSnap = await getDocs(
            collection(db, "fastLists", listRef.id, "tasks")
          );
          fastSnap.forEach((t) => batch.update(t.ref, { status: name }));
        }

        await batch.commit();
        Swal.fire({
          title: "¡Lista renombrada!",
          icon: "success",
          scrollbarPadding: false,
        });
        return true;
      }

      /* ------------------------------------------------------------------ */
      /*  CREAR                                                             */
      /* ------------------------------------------------------------------ */
      if (!dup.empty) {
        await Swal.fire({
          title: "Ya existe esa lista",
          icon: "error",
          scrollbarPadding: false,
        });
        return false;
      }

      await addDoc(listsRef, {
        name,
        color,
        projectId: projectId || null,
        ownerId: ownerUid, // ← aquí estaba el error
        createdAt: serverTimestamp(),
      });
      Swal.fire({
        title: "¡Lista creada!",
        icon: "success",
        scrollbarPadding: false,
      });
      return true;
    } catch (err) {
      console.error("createOrUpdateList:", err);
      await Swal.fire("Error al guardar la lista", "", "error");
      return false;
    }
  };

  /* ----------------------------------------------------------------------- */
  /*  CRUD TAREA                                                             */
  /* ----------------------------------------------------------------------- */
  const createTask = async (taskData) => {
    if (!ownerUid) return Swal.fire("Error", "No se detectó usuario", "error");

    let refPath;
    if (taskData.projectId) refPath = collection(db, "tareas");
    else if (taskData.listId)
      refPath = collection(db, "fastLists", taskData.listId, "tasks");
    else return Swal.fire("Error", "Falta referencia de lista", "error");

    try {
      const ref = await addDoc(refPath, {
        ...taskData,
        ownerId: ownerUid,
        userId: user.uid,
        userName:
          user.displayName ||
          `${user.nombre || ""} ${user.apellido || ""}`.trim() ||
          "Sin nombre",
        avatarUrl: user.photoURL || "/img/avatar2.jpg",
        createdAt: serverTimestamp(),
        order: typeof taskData.order === "number" ? taskData.order : Date.now(),
      });
      Swal.fire("¡Tarea agregada!", "", "success");
      return ref.id;
    } catch (err) {
      console.error("createTask:", err);
      Swal.fire("Error", "No se pudo crear la tarea", "error");
    }
  };

  const updateTask = async (taskId, newData, message = "Tarea actualizada") => {
    try {
      // TAREA RÁPIDA (vive en fastLists/{listId}/tasks)
      if (newData.isFast) {
        const fromListId = newData.prevListId || newData.listId; // origen
        const toListId = newData.listId; // destino

        if (fromListId && toListId && fromListId !== toListId) {
          const batch = writeBatch(db);
          const src = doc(db, "fastLists", fromListId, "tasks", taskId);
          const dst = doc(db, "fastLists", toListId, "tasks", taskId); // mismo id

          // CREA/ACTUALIZA en destino con ownerId correcto
          batch.set(
            dst,
            { ...newData, listId: toListId, ownerId: ownerUid },
            { merge: true }
          );

          // BORRA en origen
          batch.delete(src);

          await batch.commit();
          return taskId;
        }

        // Misma lista → update normal
        const taskDoc = doc(db, "fastLists", toListId, "tasks", taskId);
        await updateDoc(taskDoc, { ...newData, ownerId: ownerUid });
        return taskId;
      }

      // TAREA NORMAL (colección raíz "tareas")
      const taskDoc = doc(db, "tareas", taskId);
      await updateDoc(taskDoc, { ...newData, ownerId: ownerUid });
      return taskId;
    } catch (err) {
      console.error("updateTask:", err);
      Swal.fire("Error al actualizar tarea", err.message, "error");
    }
  };

  const deleteTask = async (taskId, { isFast = false, listId = null } = {}) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar tarea?",
      icon: "warning",
      showCancelButton: true,
    });
    if (!isConfirmed) return;

    const taskDoc =
      isFast && listId
        ? doc(db, "fastLists", listId, "tasks", taskId)
        : doc(db, "tareas", taskId);

    await deleteDoc(taskDoc);
    Swal.fire("¡Tarea eliminada!", "", "success");
  };

  /* ----------------------------------------------------------------------- */
  /*  deleteList (normales y fastLists)                                      */
  /* ----------------------------------------------------------------------- */
  const deleteList = async (listName, projectId) => {
    const { isConfirmed } = await Swal.fire({
      title: `¿Eliminar la lista “${listName}”?`,
      text: "Se eliminarán también todas las tareas que contiene.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      scrollbarPadding: false,
    });
    if (!isConfirmed) return;

    const colList = projectId ? "list" : "fastLists";

    try {
      const listSnap = await getDocs(
        query(
          collection(db, colList),
          where("name", "==", listName),
          where("ownerId", "==", ownerUid),
          projectId
            ? where("projectId", "==", projectId)
            : where("projectId", "==", null)
        )
      );
      if (listSnap.empty)
        return Swal.fire("Error", "No se encontró la lista", "error");

      const listRef = listSnap.docs[0].ref;
      const batch = writeBatch(db);

      if (projectId) {
        const tSnap = await getDocs(
          query(
            collection(db, "tareas"),
            where("status", "==", listName),
            where("projectId", "==", projectId),
            where("ownerId", "==", ownerUid)
          )
        );
        tSnap.forEach((t) => batch.delete(t.ref));
      } else {
        const fastTasks = await getDocs(
          collection(db, "fastLists", listRef.id, "tasks")
        );
        fastTasks.forEach((t) => batch.delete(t.ref));
      }

      batch.delete(listRef);
      await batch.commit();

      setListsGlobal((prev) =>
        prev.filter(
          (l) =>
            !(
              l.name === listName &&
              ((projectId && l.projectId === projectId) ||
                (!projectId && l.isFast))
            )
        )
      );

      Swal.fire({
        title: "¡Lista eliminada!",
        icon: "success",
        scrollbarPadding: false, // <-- agrégalo aquí también
      });
    } catch (err) {
      console.error("deleteList:", err);
      Swal.fire({
        title: "Error al eliminar la lista",
        text: err.message,
        icon: "error",
        scrollbarPadding: false, // <-- y aquí
      });
    }
  };

  /* ----------------------------------------------------------------------- */
  /*  Render                                                                 */
  /* ----------------------------------------------------------------------- */
  if (authLoading) return <></>;

  return (
    <TaskContext.Provider
      value={{
        listsGlobal,
        tasksGlobal,
        groupsGlobal,
        listColors,
        createOrUpdateList,
        deleteList,
        createTask,
        updateTask,
        deleteTask,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};
